import type {Config as GRPCConfig} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/config.types';
import {defaultRetry} from '../react-query';
import type {
  InferConfigType,
  InferFinalResponseType,
  InferResponseType,
  MakeAllKeysRequired,
  MarkConfigOptionalForNonBulkRequests,
} from '../types';
import {DataFetcherRequest} from '../useRequest';
import {useGrpcClientConfig} from './GrpcClientContext';
import {isClientTimeoutExceededError} from './code';
import {convertAllNullPropertiesToUndefined} from './convertAllNullPropertiesToUndefined';
import {
  GrpcQueryConfigWithSection,
  GrpcRequestServiceMethod,
  useGenericGrpcRequest,
} from './useGenericGrpcRequest';

type DeepPartial<T> = {
  [P in keyof T]?: DeepPartial<T[P]>;
};

export interface GenericTadaridaConfig {
  config?: DeepPartial<GRPCConfig>;
}

export function useGrpcRequest<
  ServiceMethod extends GrpcRequestServiceMethod,
  MethodName extends Awaited<ServiceMethod['method']>,
  ResponseType extends InferResponseType<
    ReturnType<Awaited<ReturnType<ServiceMethod>>>
  >,
  ParamsType extends MakeAllKeysRequired<
    Omit<
      Parameters<Awaited<ReturnType<ServiceMethod>>>[1],
      keyof GenericTadaridaConfig
    >
  >,
  DefaultConfigType extends GrpcQueryConfigWithSection<
    ResponseType,
    ParamsType
  >,
  GrpcConfigType extends InferConfigType<
    MethodName,
    ResponseType,
    DefaultConfigType
  >,
  TData extends object = InferFinalResponseType<GrpcConfigType, ResponseType>,
>(
  lazyAsyncService: ServiceMethod,
  params: ParamsType,
  ...[grpcQueryConfig]: MarkConfigOptionalForNonBulkRequests<
    MethodName,
    GrpcConfigType
  >
) {
  const genericConfig = useGrpcClientConfig();
  const query = useGenericGrpcRequest(
    lazyAsyncService,
    convertAllNullPropertiesToUndefined<ParamsType>(params),
    {config: genericConfig},
    {retry: grpcDefaultRetry, ...grpcQueryConfig} as GrpcConfigType,
  );

  return query as unknown as DataFetcherRequest<TData, unknown, ResponseType>;
}

const grpcDefaultRetry = (failureCount: number, error: unknown) => {
  if (!isClientTimeoutExceededError(error)) {
    return false;
  }
  return defaultRetry(failureCount, error);
};
